import { MemberProfile } from '../types/member';
import { seasons } from '../data/seasons';
import { supabase } from './supabase';
import { cacheManager } from './cache';
import { parseCSVLine } from './utils';
import { generateUUID } from './utils/uuid';

// Add real-time sync function
export async function syncMembersRealtime(members: MemberProfile[]): Promise<void> {
  if (!supabase) {
    console.warn('Supabase not configured, skipping sync');
    return;
  }

  // Set up real-time channel
  const channel = supabase.channel('members-sync')
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'members' },
      (payload) => {
        console.log('Member change detected:', payload);
      }
    )
    .subscribe();

  try {
    // Process each member in parallel
    await Promise.all(members.map(async (member) => {
      // Skip Alan Pham and Jesse Nguyen
      if (member.name === 'Alan Pham' || member.name === 'Jesse Nguyen') {
        return;
      }

      try {
        // Format tournament scores
        const formattedScores = member.tournament_scores?.map(score => ({
          tournament_id: score.tournament_id || generateUUID(),
          score: score.score,
          date: score.date,
          tournament_name: score.tournament_name
        })) || [];

        // Normalize member status
        const currentStatus = member.currentStatus === 'M' || member.currentStatus === 'NVM' || member.currentStatus === 'inactive'
          ? member.currentStatus 
          : 'inactive';

        const membershipStatus = currentStatus === 'M' 
          ? 'active' 
          : currentStatus === 'NVM'
          ? 'non-voting'
          : 'inactive';

        // Update member data
        const { error } = await supabase
          .from('members')
          .upsert({
            id: member.id,
            name: member.name.trim(),
            email: member.email || null,
            handicap_index: member.handicap,
            flight: member.flight === 'Sr.' ? 'Sr' : member.flight,
            join_date: member.joinDate,
            tournament_scores: formattedScores,
            total_rounds: member.total_rounds || 0,
            membership_status: membershipStatus,
            is_senior: member.isSenior,
            current_status: currentStatus,
            preferences: member.preferences || { preferred_tees: null, walking: false, cart: true }
          }, {
            onConflict: 'name'
          });

        if (error) throw error;

      } catch (error) {
        console.error(`Failed to sync member ${member.name}:`, error);
      }
    }));

  } catch (error) {
    console.error('Failed to sync members:', error);
  } finally {
    // Clean up subscription
    channel.unsubscribe();
  }
}

export const CACHE_KEY = 'members_data';
const BATCH_SIZE = 100;
const RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 1000;

// Get member status for a specific date
export function getMemberStatusForDate(member: MemberProfile, date: string): {
  status: 'M' | 'NVM' | 'inactive';
  flight?: string;
  handicap?: number;
} {
  if (!member.seasons?.length) {
    return {
      status: member.current_status || 'inactive',
      flight: member.flight,
      handicap: member.handicap_index
    };
  }

  // Find the season that contains this date
  const season = member.seasons.find(s => {
    const seasonStart = new Date(s.startDate);
    const seasonEnd = new Date(s.endDate);
    const targetDate = new Date(date);
    return targetDate >= seasonStart && targetDate <= seasonEnd;
  });

  return {
    status: season?.status || 'inactive',
    flight: season?.flight || member.flight,
    handicap: season?.handicap || member.handicap_index
  };
}

// Get member season info
export function getMemberSeason(member: MemberProfile, year: number): {
  status: 'M' | 'NVM' | 'inactive';
  flight?: string;
  handicap?: number;
} | undefined {
  const season = member.seasons?.find(s => {
    const seasonYear = new Date(s.startDate).getFullYear();
    return seasonYear === year;
  });

  if (!season) {
    return undefined;
  }

  return {
    status: season.status,
    flight: season.flight,
    handicap: season.handicap
  };
}

// Helper to parse tournament dates from header
function parseTournamentDates(headers: string[]): { name: string; date: string }[] {
  const FIXED_COLUMNS = 6; // Name,Email,Current Status,Handicap,Flight,Seniors
  const TOURNAMENT_HEADER_REGEX = /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+'(\d{2})\s+(.+)/i;
  
  // Get tournament headers starting after fixed columns
  const tournamentHeaders = headers.slice(FIXED_COLUMNS);
  
  // Parse tournament dates from headers
  return tournamentHeaders.map(header => {
    const match = header.match(TOURNAMENT_HEADER_REGEX);
    if (match) {
      const [_, month, year, courseName] = match;
      const monthMap: Record<string, string> = {
        'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04',
        'May': '05', 'Jun': '06', 'Jul': '07', 'Aug': '08',
        'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
      };
      // Handle year 2025 correctly
      const fullYear = parseInt(year) < 25 ? `20${year}` : `20${year}`;
      return {
        name: header.trim(),
        date: `${fullYear}-${monthMap[month]}-01`
      };
    }
    return null;
  }).filter((date): date is NonNullable<typeof date> => date !== null);
}

// Helper to parse scores for a member
function parseMemberScores(scores: string[], tournamentDates: { name: string; date: string }[]): {
  tournament_scores: Array<{
    tournament_id: string;
    score: number;
    date: string;
    tournament_name: string;
  }>;
  total_rounds: number;
} {
  const tournament_scores = scores.map((score, index) => {
    const tournament = tournamentDates[index];
    if (!tournament || !score || score === '' || score === '200') {
      return null;
    }
    
    const scoreNum = parseInt(score.trim());
    if (isNaN(scoreNum)) {
      return null;
    }
    
    return {
      tournament_id: generateUUID(),
      score: scoreNum,
      date: tournament.date,
      tournament_name: tournament.name
    };
  }).filter((score): score is NonNullable<typeof score> => score !== null);

  return {
    tournament_scores,
    total_rounds: tournament_scores.length
  };
}

export async function syncMemberProfile(member: MemberProfile): Promise<void> {
  if (!supabase) {
    console.warn('Supabase not configured, skipping sync');
    return;
  }
  
  // Cache invalidation
  cacheManager.clear(CACHE_KEY);

  // Validate required fields
  if (!member.name?.trim()) {
    throw new Error('Member name is required');
  }

  // Normalize flight value
  if (member.flight === 'Sr.') {
    member.flight = 'Sr';
  }

  // Validate flight value
  if (member.flight && !['Championship', 'A', 'B', 'C', 'Sr'].includes(member.flight)) {
    member.flight = null;
  }

  // Validate status
  if (member.currentStatus && !['M', 'NVM', 'inactive'].includes(member.currentStatus)) {
    member.currentStatus = 'inactive';
  }

  try {
    // First check if member exists in current state
    const { data: existingMember, error: lookupError } = await supabase
      .from('members')
      .select('*')
      .eq('name', member.name)
      .single();

    if (lookupError) {
      if (!lookupError.message.includes('No rows found')) {
        console.error('Error checking for existing member:', JSON.stringify(lookupError, null, 2));
        throw lookupError;
      }
    }

    // If member exists, use their ID
    if (existingMember) {
      console.log('Found existing member:', JSON.stringify(existingMember, null, 2));
      if (existingMember.id !== member.id) {
        member.id = existingMember.id;
        console.log('Using existing member ID:', {
          name: member.name,
          id: member.id
        });
      }
    }

    // Format tournament scores for Supabase
    const formattedScores = member.tournament_scores?.map(score => ({
      tournament_id: score.tournament_id || generateUUID(),
      score: score.score,
      date: score.date,
      tournament_name: score.tournament_name
    })) || [];

    // Validate and normalize member status
    const currentStatus = member.currentStatus === 'M' || member.currentStatus === 'NVM' || member.currentStatus === 'inactive'
      ? member.currentStatus 
      : 'inactive'; // Default to 'inactive' if invalid

    // Set membership status based on current status
    const membershipStatus = currentStatus === 'M' 
      ? 'active' 
      : currentStatus === 'NVM'
      ? 'non-voting'
      : 'inactive';
    // Update member data including tournament scores
    const memberData = {
      id: member.id,
      name: member.name.trim(), // Ensure name is trimmed
      email: member.email || null,
      handicap_index: member.handicap,
      flight: member.flight,
      join_date: member.joinDate,
      tournament_scores: formattedScores,
      total_rounds: member.total_rounds || 0,
      membership_status: membershipStatus,
      is_senior: member.isSenior,
      current_status: currentStatus,
      preferences: member.preferences || { preferred_tees: null, walking: false, cart: true }
    };

    const { error } = await supabase
      .from('members')
      .upsert(memberData, {
        onConflict: 'name',
        ignoreDuplicates: false
      });

    if (error) {
      // Only throw for non-connection errors
      if (!error.message?.includes('Failed to fetch')) {
        console.error('Supabase upsert error:', JSON.stringify(error, null, 2));
        throw error;
      }
    }

    // Verify the sync
    const { data: syncedMember, error: verifyError } = await supabase
      .from('members')
      .select('*')
      .eq('id', member.id)
      .single();

    if (verifyError) {
      console.error('Failed to verify sync:', verifyError);
    } else {
      console.log('Verified synced member data:', {
        name: syncedMember.name,
        id: syncedMember.id,
        scores: syncedMember.tournament_scores?.length || 0, 
        totalRounds: syncedMember.total_rounds,
        scoreDetails: syncedMember.tournament_scores?.map(s => ({
          date: s.date,
          score: s.score,
          tournament: s.tournament_name
        }))
      });
    }
  } catch (error) {
    // Only log actual errors, not connection issues
    if (error instanceof Error && 
        !error.message.includes('Failed to fetch') &&
        !error.message.includes('not configured')) {
      console.error('Failed to sync member:', JSON.stringify(error, null, 2));
    } else {
      console.log('Connection unavailable, skipping sync');
    }
    return;
  }
}

// Add optimistic update helper
export function updateMemberLocally(member: MemberProfile, skipCache = false): void {
  const cachedMembers = cacheManager.get<MemberProfile[]>(CACHE_KEY);
  if (cachedMembers && !skipCache) {
    const updatedMembers = cachedMembers.map(m => 
      m.id === member.id ? member : m
    );
    cacheManager.set(CACHE_KEY, updatedMembers);
  }
}

export async function syncAllMembers(members: MemberProfile[]): Promise<void> {
  if (!supabase) {
    console.error('Supabase not configured, skipping sync');
    return;
  }
  
  // Cache invalidation
  cacheManager.clear(CACHE_KEY);

  // Validate members array
  if (!Array.isArray(members)) {
    throw new Error('Invalid members data');
  }

  // Filter out invalid members
  members = members.filter(m => m.name?.trim());

  console.log('Starting batch sync of', members.length, 'members');

  try {
    // Get existing members to check for duplicates
    const { data: existingMembers, error: fetchError } = await supabase
      .from('members')
      .select('*');

    if (fetchError) throw fetchError;

    // Create a map of existing members by name
    const existingMemberNames = new Set(existingMembers?.map(m => m.name.toLowerCase()) || []);

    console.log('Found existing members:', existingMembers?.map(m => m.name) || []);

    // Filter out members that already exist
    const newMembers = members.filter(member =>
      !existingMemberNames.has(member.name.toLowerCase())
    );

    console.log(`Found ${existingMembers?.length || 0} existing members`);
    console.log(`Adding ${newMembers.length} new members`);
    console.log('New members:', newMembers.map(m => m.name));

    if (newMembers.length === 0) {
      console.log('No new members to sync');
      // Update cache with existing members
      cacheManager.set(CACHE_KEY, existingMembers || []);
      return;
    }

    // Process in smaller batches
    for (let i = 0; i < newMembers.length; i += SYNC_BATCH_SIZE) {
      const batch = newMembers.slice(i, i + SYNC_BATCH_SIZE);
      console.log(`Syncing batch ${Math.floor(i/SYNC_BATCH_SIZE) + 1}/${Math.ceil(newMembers.length/SYNC_BATCH_SIZE)}:`, {
        batchSize: batch.length,
        members: batch.map(m => m.name)
      });

      const { error } = await supabase
        .from('members')
        .upsert(
          batch.map(m => ({
            id: m.id,
            name: m.name,
            email: m.email,
            handicap_index: m.handicap,
            flight: m.flight,
            join_date: m.joinDate,
            membership_status: m.membershipStatus,
            is_senior: m.isSenior,
            current_status: m.currentStatus,
            preferences: m.preferences
          })),
          {
            onConflict: 'id',
            ignoreDuplicates: true,
            count: 'exact'
          }
        );

      if (error) {
        if (error.code === '23505') {
          console.warn('Duplicate members found in batch, continuing...');
          console.warn('Duplicate error details:', JSON.stringify(error, null, 2));
          continue;
        }
        throw error;
      }

      console.log(`Successfully synced batch ${Math.floor(i/BATCH_SIZE) + 1}:`, {
        members: batch.map(m => m.name)
      });
      
      // Add delay between batches
      if (i + SYNC_BATCH_SIZE < newMembers.length) {
        await new Promise(resolve => setTimeout(resolve, SYNC_DELAY));
      }
    }

    // Update cache with all members after sync
    const allMembers = [...existingMembers || [], ...newMembers];
    if (!skipCache) {
      cacheManager.set(CACHE_KEY, allMembers);
    }

  } catch (error) {
    // Only log actual errors, not connection issues
    if (error instanceof Error && 
        !error.message.includes('Failed to fetch')) {
      console.error('Failed to sync members:', JSON.stringify(error, null, 2));
    } else {
      console.log('Connection unavailable, skipping sync');
    }
  }
}

// Function to load members directly from database
export async function loadMembersFromDatabase(): Promise<MemberProfile[]> {
  if (!supabase) {
    console.log('Supabase not configured, using local data');
    return [];
  }

  try {
    const { data: members, error } = await supabase
      .from('members')
      .select('*')
      .order('name');

    if (error) {
      // Only throw for non-connection errors
      if (!error.message?.includes('Failed to fetch')) {
        throw error;
      }
      console.log('Connection unavailable, using local data');
      return [];
    }

    return members.map(m => ({
      id: m.id,
      name: m.name,
      email: m.email,
      handicap: m.handicap_index,
      flight: m.flight,
      currentStatus: m.current_status,
      isSenior: m.is_senior,
      joinDate: m.join_date,
      membershipStatus: m.membership_status,
      preferences: m.preferences,
      tournament_scores: m.tournament_scores || [],
      total_rounds: m.total_rounds || 0
    }));
  } catch (error) {
    console.error('Failed to load members from database:', error);
    throw error;
  }
}

// Function to calculate handicap index
export function calculateHandicapIndex(scores: { score: number }[]): number {
  if (!scores || scores.length < 2) return 0;

  // Get last 20 scores
  const recentScores = scores.slice(-20);
  const numScores = recentScores.length;

  // Determine how many scores to use
  let numScoresToUse = 0;
  if (numScores <= 2) return 0;
  else if (numScores <= 5) numScoresToUse = 1;
  else if (numScores <= 8) numScoresToUse = 2;
  else if (numScores <= 11) numScoresToUse = 3;
  else if (numScores <= 14) numScoresToUse = 4;
  else if (numScores <= 16) numScoresToUse = 5;
  else if (numScores <= 18) numScoresToUse = 6;
  else if (numScores === 19) numScoresToUse = 7;
  else numScoresToUse = 8;

  // Sort scores from lowest to highest
  const sortedScores = [...recentScores].sort((a, b) => a.score - b.score);

  // Get lowest scores
  const lowestScores = sortedScores.slice(0, numScoresToUse);

  // Calculate average
  const average = lowestScores.reduce((sum, score) => sum + score.score, 0) / numScoresToUse;

  // Calculate index (average - 72) rounded to nearest tenth
  return Math.round((average - 72) * 10) / 10;
}

// Add function to get cached members
export function getCachedMembers(): MemberProfile[] | null {
  const cached = cacheManager.get<MemberProfile[]>(CACHE_KEY);
  return cached ? cached.map(member => ({
    ...member,
    tournament_scores: member.tournament_scores || [],
    total_rounds: member.total_rounds || 0
  })) : null;
}

// Helper to display member scores
export function displayMemberScores(memberName: string, members: MemberProfile[]): void {
  const member = members.find(m => m.name === memberName);
  if (!member) {
    console.log(`Member ${memberName} not found`);
    return;
  }

  console.log('\nMember Profile:');
  console.log('--------------');
  console.log(`Name: ${member.name}`);
  console.log(`Status: ${member.currentStatus}`);
  console.log(`Handicap: ${member.handicap}`);
  console.log(`Flight: ${member.flight}`);
  console.log(`Senior: ${member.isSenior ? 'Yes' : 'No'}`);
  console.log(`Total Rounds: ${member.total_rounds}`);
  
  if (member.tournament_scores && member.tournament_scores.length > 0) {
    console.log('\nTournament Scores:');
    console.log('----------------');
    member.tournament_scores
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .forEach(score => {
        console.log(`${score.tournament_name}: ${score.score} (${score.date})`);
      });
  } else {
    console.log('\nNo tournament scores found');
  }
}

// Helper function to generate seasons data from tournament scores
function generateSeasons(
  tournament_scores: Array<{
    tournament_id: string;
    score: number;
    date: string;
    tournament_name: string;
  }>,
  currentStatus: string | null,
  currentFlight: string | null,
  currentHandicap: number | null
): MemberProfile['seasons'] {
  // Group scores by season
  const seasonScores = tournament_scores.reduce((acc, score) => {
    const scoreDate = new Date(score.date);
    const season = seasons.find(s => {
      const startDate = new Date(s.startDate);
      const endDate = new Date(s.endDate);
      return scoreDate >= startDate && scoreDate <= endDate;
    });

    if (season) {
      if (!acc[season.year]) {
        acc[season.year] = {
          year: season.year,
          startDate: season.startDate,
          endDate: season.endDate,
          scores: []
        };
      }
      acc[season.year].scores.push(score);
    }
    return acc;
  }, {} as Record<number, any>);

  // Convert to array and add status/flight/handicap
  return Object.values(seasonScores).map(season => ({
    year: season.year,
    startDate: season.startDate,
    endDate: season.endDate,
    status: currentStatus === 'M' ? 'M' : currentStatus === 'NVM' ? 'NVM' : 'inactive',
    flight: currentFlight,
    handicap: currentHandicap
  }));
}

export function parseMembersData(csvContent: string): MemberProfile[] {
  const lines = csvContent.split('\n').filter(line => line.trim());
  const members: MemberProfile[] = [];
  const seenMembers = new Map<string, string>();
  const localMembers = getCachedMembers() || [];
  const FIXED_COLUMNS = 6; // Name,Email,Current Status,Handicap,Flight,Seniors
  
  // Parse header row to get tournament dates
  const headerRow = lines[0].split(',').map(h => h.trim());
  const totalColumns = headerRow.length;

  // Parse tournament dates
  const tournamentDates = parseTournamentDates(headerRow);

  console.log('Tournament dates:', {
    count: tournamentDates.length,
    first: tournamentDates[0],
    last: tournamentDates[tournamentDates.length - 1]
  });

  // Skip header row
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    // Split line into values
    const values = parseCSVLine(line);
    
    // Ensure we have all columns
    while (values.length < totalColumns) {
      values.push('');
    }

    const basicInfo = values.slice(0, FIXED_COLUMNS);
    const scores = values.slice(FIXED_COLUMNS).map(v => v.replace(/^"|"$/g, ''));

    // Extract basic info
    const name = basicInfo[0];
    const email = basicInfo[1];
    const status = basicInfo[2] || null;
    const handicapStr = basicInfo[3];
    const handicap = handicapStr ? parseFloat(handicapStr) : null;
    const flight = basicInfo[4] || null;
    const isSenior = basicInfo[5]?.toLowerCase() === 'yes' || basicInfo[5]?.toLowerCase() === 'true';

    if (!name?.trim()) continue;
    
    // Skip Alan Pham and Jesse Nguyen
    if (name === 'Alan Pham' || name === 'Jesse Nguyen') continue;

    const normalizedName = name.toLowerCase().trim();
    
    // Check if member already exists in current data
    const existingMember = members.find(m => 
      m.name.toLowerCase().trim() === normalizedName
    );

    if (existingMember) {
      continue;
    }
    
    // Get or generate member ID
    let memberId = seenMembers.get(normalizedName);
    if (!memberId) {
      // Check if member exists in current state
      const existingStateMember = localMembers.find(m => 
        m.name.toLowerCase().trim() === normalizedName
      );
      
      if (existingStateMember) {
        memberId = existingStateMember.id;
      } else {
        // Only generate new ID if member doesn't exist
        memberId = generateUUID();
      }
      seenMembers.set(normalizedName, memberId);
    }

    // Parse scores
    const { tournament_scores, total_rounds } = parseMemberScores(scores, tournamentDates);

    // Generate seasons data based on tournament scores
    const seasons = generateSeasons(tournament_scores, status, flight, handicap);

    const member: MemberProfile = {
      id: memberId,
      name,
      email: email || null,
      currentStatus: status === 'M' ? 'M' : status === 'NVM' || status === 'NV' ? 'NVM' : null,
      handicap_index: handicapStr && !isNaN(parseFloat(handicapStr)) ? parseFloat(handicapStr) : null,
      flight: flight === 'Sr.' ? 'Sr' : ['Championship', 'A', 'B', 'C', 'Sr'].includes(flight) ? flight : null,
      isSenior: isSenior || flight === 'Sr' || flight === 'Sr.',
      joinDate: new Date().toISOString(),
      membershipStatus: status === 'M' ? 'active' : status === 'NVM' || status === 'NV' ? 'non-voting' : 'inactive',
      preferences: { preferred_tees: null, walking: false, cart: true },
      tournament_scores,
      total_rounds,
      seasons
    };

    members.push(member);
  }

  return members;
}